// Acessórios. GABARITO da Onda 0: 'oculos' é o modelo; os demais ids de ACCESSORY_IDS
// (src/core/look.ts) seguem o mesmo formato.
//
// Âncora 'head' (16×12: óculos, fone, boné, gorro, barba, bigode, brinco, laço) ou 'torso'
// (16×8: crachá, lenço; só colunas 4–11, como a roupa). Paleta: o/a/A/H = acc0..acc3 (cor
// do acessório), w = white3, g = glass3, k = black1, i = ink.

import { sprite } from '../pix.js';
import type { DirSet, OverlayDef, PixelSprite } from '../types.js';

const ACC = { o: 'acc0', a: 'acc1', A: 'acc2', H: 'acc3', w: 'white3', g: 'glass3', k: 'black1', i: 'ink' };
const B = '.'.repeat(16);

/** Um acessório a partir de quadros digitados (cabeça 16×12 ou tronco 16×8). */
function accessory(id: string, name: string, anchor: 'head' | 'torso', frames: DirSet<readonly string[]>): OverlayDef {
  const f = (rows: readonly string[]): PixelSprite => sprite(rows, ACC);
  return { id, name, anchor, frames: { down: f(frames.down), up: f(frames.up), left: f(frames.left) } };
}

export const ACCESSORIES: readonly OverlayDef[] = [
  {
    id: 'oculos',
    name: 'Óculos',
    anchor: 'head',
    frames: {
      down: sprite(
        [
          B,
          B,
          B,
          B,
          B,
          '...kkkkkkkkkk...',
          '...k..k..k..k...',
          '...kg.k..kg.k...',
          '...kkkk..kkkk...',
          B,
          B,
          B,
        ],
        ACC,
      ),
      up: sprite(
        [B, B, B, B, B, '..k..........k..', B, B, B, B, B, B],
        ACC,
      ),
      left: sprite(
        [
          B,
          B,
          B,
          B,
          B,
          '..kkkkkkkkkk....',
          '..k..k..........',
          '..kg.k..........',
          '..kkkk..........',
          B,
          B,
          B,
        ],
        ACC,
      ),
    },
  },
  // Óculos escuros: lentes escuras cobrem os olhos de propósito, com brilho no canto.
  accessory('oculos-escuros', 'Óculos escuros', 'head', {
    down: [
      B,
      B,
      B,
      B,
      B,
      '...kkkkkkkkkk...',
      '...kaaakkaaak...',
      '...kwaakkwaak...',
      '....kkkkkkkk....',
      B,
      B,
      B,
    ],
    up: [B, B, B, B, B, '..k..........k..', B, B, B, B, B, B],
    left: [
      B,
      B,
      B,
      B,
      B,
      '..kkkkkkkkkkkk..',
      '..kaaak.........',
      '..kwaak.........',
      '..kkkkk.........',
      B,
      B,
      B,
    ],
  }),
  // Fone: arco por cima da cabeça e conchas nas orelhas.
  accessory('fone', 'Fone', 'head', {
    down: [
      '....AAAAAAAA....',
      '...Aa........aA.',
      '..Aa..........aA',
      'oAa..........aAo',
      'oAa..........aAo',
      'oAa..........aAo',
      'oAo..........oAo',
      B,
      B,
      B,
      B,
      B,
    ],
    up: [
      '....AAAAAAAA....',
      '...Aa........aA.',
      '..Aa..........aA',
      'oAa..........aAo',
      'oAa..........aAo',
      'oAo..........oAo',
      B,
      B,
      B,
      B,
      B,
      B,
    ],
    left: [
      '....AAAAAAAAA...',
      '...Aa.........Ao',
      '..Aa..........Ao',
      '......oAAo......',
      '......oAAo......',
      '......oAAo......',
      '......oAAo......',
      B,
      B,
      B,
      B,
      B,
    ],
  }),
  // Headset: fone com microfone na frente, perto da boca.
  accessory('headset', 'Headset', 'head', {
    down: [
      '....AAAAAAAA....',
      '...Aa........aA.',
      '..Aa..........aA',
      'oAa..........aAo',
      'oAa..........aAo',
      'oAa..........aAo',
      'oAo..........oAo',
      '..ok............',
      '...kkk..........',
      B,
      B,
      B,
    ],
    up: [
      '....AAAAAAAA....',
      '...Aa........aA.',
      '..Aa..........aA',
      'oAa..........aAo',
      'oAa..........aAo',
      'oAo..........oAo',
      B,
      B,
      B,
      B,
      B,
      B,
    ],
    left: [
      '....AAAAAAAAA...',
      '...Aa.........Ao',
      '..Aa..........Ao',
      '......oAAo......',
      '......oAAo......',
      '......oAAo......',
      '......oAAo......',
      '...kk...........',
      '..kk............',
      B,
      B,
      B,
    ],
  }),
  // Boné: copa de cor sólida e aba pra frente, na altura da testa.
  accessory('bone', 'Boné', 'head', {
    down: [
      '.....oHHHHHo....',
      '....oHaaaaaAo...',
      '...oHaaaaaaaAo..',
      '..oHaaaaaaaaaAo.',
      '.oAAAAAAAAAAAAo.',
      '.oooooooooooooo.',
      B,
      B,
      B,
      B,
      B,
      B,
    ],
    up: [
      '.....oHHHHHo....',
      '....oHaaaaaAo...',
      '...oHaaaaaaaAo..',
      '..oHaaaaaaaaaAo.',
      '..oAAAAAAAAAAAo.',
      '...oooooooooo...',
      B,
      B,
      B,
      B,
      B,
      B,
    ],
    left: [
      '.....oHHHHHo....',
      '....oHaaaaaAo...',
      '...oHaaaaaaaAo..',
      '.oAAaaaaaaaaaAo.',
      'oooooooooooooo..',
      B,
      B,
      B,
      B,
      B,
      B,
      B,
    ],
  }),
  // Gorro: cúpula de malha com a barra canelada logo acima das sobrancelhas.
  accessory('gorro', 'Gorro', 'head', {
    down: [
      '....oooooooo....',
      '...oaaaaaaaao...',
      '..oaaHaaaaaaao..',
      '..oaaaaaaaaaaao.',
      '..oAAAAAAAAAAAo.',
      '..oAoAoAoAoAoAo.',
      B,
      B,
      B,
      B,
      B,
      B,
    ],
    up: [
      '....oooooooo....',
      '...oaaaaaaaao...',
      '..oaaaaaaaaaaao.',
      '..oaaaaaaaaaaao.',
      '..oAAAAAAAAAAAo.',
      '..oAoAoAoAoAoAo.',
      B,
      B,
      B,
      B,
      B,
      B,
    ],
    left: [
      '....oooooooo....',
      '...oaaaaaaaao...',
      '..oaaaaaaaaaaao.',
      '..oaaaaaaaaaaao.',
      '..oAAAAAAAAAAo..',
      '..oAoAoAoAoAoo..',
      B,
      B,
      B,
      B,
      B,
      B,
    ],
  }),
  // Barba: cobre o queixo, deixando a boca à mostra.
  accessory('barba', 'Barba', 'head', {
    down: [
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      '..a..........a..',
      '...aaa....aaa...',
      '..aaaaaaaaaaaa..',
      '...AAAAAAAAAA...',
    ],
    up: [
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      '..a..........a..',
      '..aaaaaaaaaaaa..',
      '...AAAAAAAAAA...',
      B,
    ],
    left: [
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      '..aa.........a..',
      '..aaaa.......a..',
      '...aaaaaaaaaa...',
      '....AAAAAAAA....',
    ],
  }),
  // Bigode: fio logo acima da boca, sem tampar a boca.
  accessory('bigode', 'Bigode', 'head', {
    down: [B, B, B, B, B, B, B, B, '....aaaaaaaa....', B, B, B],
    up: [B, B, B, B, B, B, B, B, B, B, B, B],
    left: [B, B, B, B, B, B, B, B, '...aaa..........', B, B, B],
  }),
  // Crachá: cartão branco no peito, com faixa da cor do acessório.
  accessory('cracha', 'Crachá', 'torso', {
    down: [B, B, '.....owwwo......', '.....owAwo......', '.....owwwo......', '.....oooooo.....', B, B],
    up: [B, B, B, B, B, B, B, B],
    left: [B, B, '......owwo......', '......owAo......', '......owwo......', '......oooo......', B, B],
  }),
  // Lenço: faixa no pescoço com a ponta caindo no peito.
  accessory('lenco', 'Lenço', 'torso', {
    down: ['....AaAaAaAa....', '....aAaAaAaA....', '.........aAa....', '.........aAa....', '.........aAo....', '..........o.....', B, B],
    up: ['....AaAaAaAa....', '....aAaAaAaA....', B, B, B, B, B, B],
    left: ['.....AaAaAaA....', '.....aAaAaAa....', '.....aAa........', '.....aAa........', '.....aAo........', B, B, B],
  }),
  // Brinco: pingente pequeno, só em uma orelha por vista.
  accessory('brinco', 'Brinco', 'head', {
    down: [B, B, B, B, B, B, B, B, '.............H..', '.............a..', B, B],
    up: [B, B, B, B, B, B, B, B, '..H..........H..', '..a..........a..', B, B],
    left: [B, B, B, B, B, B, B, B, '...H............', '...a............', B, B],
  }),
  // Laço: laço de fita no alto da cabeça, do lado direito.
  accessory('laco', 'Laço', 'head', {
    down: [
      '.....oo..oo.....',
      '....oAAoooAAo...',
      '....oAaaaaAo....',
      '.....ooAAAoo....',
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      B,
    ],
    up: [
      '.....oo..oo.....',
      '....oAAoooAAo...',
      '....oAaaaaAo....',
      '.....ooAAAoo....',
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      B,
    ],
    left: [
      '.....oo..oo.....',
      '....oAAoooAAo...',
      '....oAaaaaAo....',
      '.....ooAAAoo....',
      B,
      B,
      B,
      B,
      B,
      B,
      B,
      B,
    ],
  }),
];
